/*
 * Auto free gift
 * Keeps exactly one gift line in the cart while the qualifying subtotal is at or above
 * the threshold, and removes it when the cart drops below. Configured by
 * snippets/auto-free-gift.liquid. The gift is made ₹0 by the Shopify "Buy X get Y"
 * automatic discount — this script only manages the cart line.
 */
(() => {
  const config = window.autoFreeGift;
  if (!config || !Number(config.variantId)) return;

  const GIFT_VARIANT_ID = Number(config.variantId);
  const THRESHOLD = Number(config.threshold);
  const STATE_KEY = 'autoFreeGift:state';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  const SYNC_DELAY = 300;

  // Our own requests go through the fetch that existed before we patched it,
  // so they never trigger another sync.
  const originalFetch = window.fetch;
  let timer = null;
  let running = null;
  let rerun = false;
  let changeStreak = 0;
  let giftUnavailable = false;

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {}
  };

  const isGift = (item) => item.variant_id === GIFT_VARIANT_ID;

  // Matches the gift's row in the cart markup: the quantity input or the product link.
  const GIFT_IN_DOM = `[data-quantity-variant-id="${GIFT_VARIANT_ID}"], a[href*="variant=${GIFT_VARIANT_ID}"]`;
  const GIFT_QUANTITY_INPUT = `input[data-quantity-variant-id="${GIFT_VARIANT_ID}"]`;

  // True when the cart page shows a different gift state than the cart itself, e.g. it was
  // rendered by the add-to-cart redirect before the gift request from the previous page landed.
  const cartPageOutOfDate = (cart) => {
    const items = document.getElementById('main-cart-items');
    if (!items) return false;
    return Boolean(items.querySelector(GIFT_IN_DOM)) !== cart.items.some(isGift);
  };

  const qualifyingTotal = (cart) =>
    cart.items.filter((item) => !isGift(item)).reduce((sum, item) => sum + item.final_line_price, 0);

  // GETs send no Accept header: with "Accept: application/json", /cart?sections=…
  // returns the cart JSON instead of the rendered sections.
  const request = async (url, body) => {
    const response = await originalFetch.call(
      window,
      url,
      body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
        : { method: 'GET' }
    );
    if (!response.ok) {
      const error = new Error(`${url} responded ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  };

  const getCart = () => request(`${config.cartUrl}.js`);
  const changeLine = (key, quantity) => request(`${config.cartChangeUrl}.js`, { id: key, quantity });
  const addGift = () =>
    request(`${config.cartAddUrl}.js`, {
      items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'Yes' } }],
    });

  async function sync() {
    let cart = await getCart();
    const state = readState();
    const giftLines = cart.items.filter(isGift);
    const qualifies = cart.items.some((item) => !isGift(item)) && qualifyingTotal(cart) >= THRESHOLD;
    let added = false;

    // The gift was in a qualifying cart and is now gone while the cart still qualifies:
    // the customer removed it, so don't force it back in this session.
    if (qualifies && !giftLines.length && state.hadGift && state.qualified) state.declined = true;
    if (!qualifies) state.declined = false;

    if (!qualifies && giftLines.length) {
      for (const line of giftLines) cart = await changeLine(line.key, 0);
    } else if (qualifies && !giftLines.length && !state.declined && !giftUnavailable) {
      try {
        await addGift();
        added = true;
      } catch (error) {
        // 422 = sold out / not purchasable. Stop retrying on this page.
        if (error.status === 422) giftUnavailable = true;
        else throw error;
      }
      cart = await getCart();
    } else if (qualifies && giftLines.reduce((sum, line) => sum + line.quantity, 0) > 1) {
      const [first, ...rest] = giftLines;
      if (first.quantity !== 1) cart = await changeLine(first.key, 1);
      for (const line of rest) cart = await changeLine(line.key, 0);
    }

    const changed = JSON.stringify(cart.items.filter(isGift)) !== JSON.stringify(giftLines);
    writeState({ hadGift: cart.items.some(isGift), qualified: qualifies, declined: state.declined });

    if (changed || cartPageOutOfDate(cart)) await refreshCartUI(cart);
    if (added && config.addedMessage) showToast(config.addedMessage);
    return changed;
  }

  // The gift stays at quantity 1: disable "+" and typing on its quantity input.
  // Re-applied whenever the theme re-renders the cart. Removing it is still allowed.
  function lockGiftQuantity() {
    document.querySelectorAll(GIFT_QUANTITY_INPUT).forEach((input) => {
      input.max = 1;
      input.readOnly = true;
      const plus = input.parentElement && input.parentElement.querySelector('[name="plus"]');
      if (plus) {
        plus.disabled = true;
        plus.classList.add('disabled');
      }
    });
  }

  // Re-render the cart icon and, on the cart page, the line items and footer
  // using the Section Rendering API (same sections Dawn-based themes like Refresh use).
  async function refreshCartUI(cart) {
    const onCartPage = window.location.pathname === config.cartUrl;
    if (onCartPage && cart.item_count === 0) return window.location.reload();

    const targets = [];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ element: bubble, section: 'cart-icon-bubble', selector: '.shopify-section' });
    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element && element.dataset.id) {
        targets.push({ element: element.querySelector('.js-contents') || element, section: element.dataset.id, selector: '.js-contents' });
      }
    });
    if (!targets.length) return onCartPage ? window.location.reload() : undefined;

    try {
      const sections = await request(`${config.cartUrl}?sections=${targets.map((t) => t.section).join(',')}`);
      targets.forEach(({ element, section, selector }) => {
        if (!sections[section]) return;
        const html = new DOMParser().parseFromString(sections[section], 'text/html');
        const source = html.querySelector(selector);
        if (source) element.innerHTML = source.innerHTML;
      });
    } catch (error) {
      if (onCartPage) window.location.reload();
    }
  }

  function showToast(message) {
    const toast = document.createElement('div');
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    toast.style.cssText =
      'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;' +
      'background:#1f3d1f;color:#fff;padding:12px 18px;border-radius:8px;font-size:14px;' +
      'box-shadow:0 4px 16px rgba(0,0,0,.2);max-width:calc(100% - 32px);text-align:center;';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3500);
  }

  function runSync() {
    clearTimeout(timer);
    timer = null;
    if (running) {
      rerun = true;
      return running;
    }
    running = sync()
      .then((changed) => {
        // Check again after each change, in case a request from the previous page landed
        // at the same time (e.g. during the add-to-cart redirect). Capped to avoid fighting
        // another script that keeps undoing the change.
        changeStreak = changed ? changeStreak + 1 : 0;
        if (changed && changeStreak < 3) rerun = true;
      })
      .catch((error) => console.warn('[auto-free-gift]', error))
      .finally(() => {
        running = null;
        if (rerun) {
          rerun = false;
          runSync();
        }
      });
    return running;
  }

  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(runSync, SYNC_DELAY);
  }

  function isCartMutation(url) {
    try {
      return CART_MUTATION.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  // Watch every cart change the theme or other apps make, via fetch or XHR.
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
    const result = originalFetch.apply(this, arguments);
    if (isCartMutation(url)) {
      result.then((response) => response.ok && scheduleSync()).catch(() => {});
    }
    return result;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._autoFreeGiftUrl = url;
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (isCartMutation(this._autoFreeGiftUrl)) {
      this.addEventListener('load', () => this.status < 400 && scheduleSync());
    }
    return xhrSend.apply(this, arguments);
  };

  // If a sync is pending when the customer clicks "Check out", finish it first.
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || (!timer && !running)) return;
      event.preventDefault();
      const form = event.target;
      runSync().finally(() => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        form.appendChild(input);
        HTMLFormElement.prototype.submit.call(form);
      });
    },
    true
  );

  // Observer callbacks are already batched, and locking only changes attributes,
  // so it never re-triggers the observer.
  lockGiftQuantity();
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(lockGiftQuantity).observe(document.body, { childList: true, subtree: true });
  }

  // Cart may have changed on another page, tab, or through a non-AJAX form post.
  runSync();
  window.addEventListener('pageshow', (event) => event.persisted && runSync());
})();
